import { sourceToViewport, type Rotation } from "../coordinates/sourceToViewport";
import { drawShapes } from "../annotations/renderShapes";
import type { Shape } from "../annotations/shapes";

/**
 * renderSnapshotPng - renders the canonical frame plus its confirmed annotations into a PNG for
 * Save snapshot (D-11, T-05-85). Draws through the exact same sourceToViewport transform and
 * drawShapes call as the live overlay (05-13's FrozenFrameCanvas, 05-15's AnnotationCanvas), so
 * the PNG matches what both sides saw on screen. Always rendered unmirrored: the snapshot is the
 * canonical view, never a viewer's own mirrored display.
 */

const MAX_SNAPSHOT_BYTES = 12 * 1024 * 1024; // 12 MiB, matches the backend's saveSnapshot limit

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load the frozen frame for the snapshot."));
    // No crossOrigin attribute: imageSrc is always same-origin (the durable frame URL) or a
    // local blob URL (the fast-path cached copy), never a third-party origin.
    img.src = src;
  });
}

function displayedSize(frame: { sourceW: number; sourceH: number; rotation: Rotation }): { dw: number; dh: number } {
  if (frame.rotation === 90 || frame.rotation === 270) {
    return { dw: frame.sourceH, dh: frame.sourceW };
  }
  return { dw: frame.sourceW, dh: frame.sourceH };
}

export async function renderSnapshotPng(
  imageSrc: string,
  frame: { sourceW: number; sourceH: number; rotation: Rotation; mirrored: boolean },
  shapes: Shape[],
): Promise<Blob> {
  const img = await loadImage(imageSrc);
  const { dw, dh } = displayedSize(frame);

  const canvas = document.createElement("canvas");
  canvas.width = dw;
  canvas.height = dh;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not create a canvas context for the snapshot.");
  }

  const transform = sourceToViewport({ ...frame, mirrored: false }, { x: 0, y: 0, width: dw, height: dh });
  const { contentRect, scale } = transform;
  const cx = contentRect.x + contentRect.width / 2;
  const cy = contentRect.y + contentRect.height / 2;
  const sw = frame.sourceW * scale;
  const sh = frame.sourceH * scale;

  // Same translate -> rotate order as FrozenFrameCanvas, minus the mirror scale (never mirrored).
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((frame.rotation * Math.PI) / 180);
  ctx.drawImage(img, -sw / 2, -sh / 2, sw, sh);
  ctx.restore();

  drawShapes(ctx, shapes, transform, null);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
  if (!blob) {
    throw new Error("Could not render the snapshot image.");
  }
  if (blob.size > MAX_SNAPSHOT_BYTES) {
    throw new Error("The snapshot is too large to save.");
  }
  return blob;
}

export default renderSnapshotPng;
